
import { useParams } from 'react-router'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import DoubleTable from '../../../../../../../baseComponents/DoubleTable'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import BaseScreenTable from '../../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { useAddDoubleTableGreenRowRender } from '../../../../../../../baseComponents/DoubleTable/functions'
import { ColumnTableSchema } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { permissionCheck } from '../../../../../../../baseComponents/PermissionChecker/function'
import { CustomRenderBoolean, DeleteRow, EditRowNotLink } from '../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { TwinTableLink } from '../../../../../../../baseComponents/TwinTable'
import locations from '../../../../../../../utils/globals/locations'
import { TableComponentProps } from '../../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import WorkSheetProductStatuses from './WorkSheetProductStatuses'
import ModalCEWorkSheetGroupStatus from './ModalCEWSGroupStatus'
import { WorkSheetGroupStatusModelType } from '@teinor/erp/types/company/order/sell/workSheet/workSheetGroupStatus'
import WorkSheetGroupStatusSortButton from './WorkSheetGroupStatusSortButton'
import { useMemo } from 'react'
import { faRotateLeft } from '@fortawesome/pro-light-svg-icons'
import ModalChangeActiveWorkSheetGroupStatus from './ModalChangeActiveWorkSheetGroupStatus'

interface WorkSheetGroupStatusProps extends ComponentWithPermissions { }

const WorkSheetGroupStatus: React.FC<WorkSheetGroupStatusProps> = ({ userPermissions }) => {
    const { WorkSheetGroupStatusId } = useParams()
    return (<DoubleTable userPermissions={userPermissions} FirstChild={WorkSheetGroupStatusInner} SecondChild={WorkSheetProductStatuses} displaySecond={WorkSheetGroupStatusId !== 'main'} />)
}


const WorkSheetGroupStatusInner: React.FC<WorkSheetGroupStatusProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('workSheetGroups', 'Grupos de hojas de trabajo'),
        newText: t('newGroup', 'Nuevo grupo'),
    }
    return (
        <BaseScreenTable TableComponent={TableWorkSheetGroupStatus} CreateModalComponent={ModalCEWorkSheetGroupStatus} EditModalComponent={ModalCEWorkSheetGroupStatus} DeleteModalComponent={ModalChangeActiveWorkSheetGroupStatus} pageData={pageData} userPermissions={userPermissions} />
    )
}

interface TableWorkSheetGroupStatusProps extends TableComponentProps { }

const TableWorkSheetGroupStatus: React.FC<TableWorkSheetGroupStatusProps> = ({ tableInstance, setTableInstance, userPermissions, setOpened }) => {
    const { t } = useTwinTranslation()
    const { WorkSheetGroupStatusId } = useParams()
    const { rowRenderer } = useAddDoubleTableGreenRowRender(WorkSheetGroupStatusId || '')

    const columns: ColumnTableSchema<WorkSheetGroupStatusModelType> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            sortable: true,
            width: 75
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            minWidth: 130,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disabled', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            searchKey: 'active',
            generalSearchable: false
        },
        {
            id: 'order',
            dataKey: 'order',
            label: t('order', 'Orden'),
            width: 75
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRowNotLink key={'edit' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'edit', allRowData })} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} icon={!allRowData?.active ? faRotateLeft : undefined} messageTooltip={!allRowData?.active ? t('reactivate', 'Reactivar') : undefined} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]
    const children = useMemo(() => <WorkSheetGroupStatusSortButton tableInstance={tableInstance} userPermissions={userPermissions} />, [tableInstance, userPermissions])

    return (
        <TwinTableLink createRowDataLink={[locations.workSheetGroupStatus, ['$id']]} name='workSheetGroupStatus' columns={columns} getDataFrom='/api/app/order/sell/workSheet/workSheetGroupStatus/getAllWorkSheetGroupStatuses' setTableInstance={setTableInstance} rowRenderer={rowRenderer} defaultOrder={{ defaultOrderField: 'order', order: 'asc' }} hideLoadFiltersButton={true} hideChangeColumnsButton={true} >
            {children}
        </TwinTableLink>
    )
}


export default WorkSheetGroupStatus
